import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import VehicleCard from "../components/VehicleCard";
import DatePicker from "../components/DatePicker";
import ResumeDraftBanner from "../components/ResumeDraftBanner";
import GiveawayCampaignBanner from "../components/GiveawayCampaignBanner";
import HowItWorksDemo from "../components/HowItWorksDemo";
import { ArrowRight, Search, Shield, Sparkles, Coins, Gauge, Zap, Trophy } from "lucide-react";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import { AI_NAME } from "../lib/brand";
import { pageItems } from "../lib/pageItems";
import { VEHICLE_BODY_TYPES } from "../lib/vehicleTypes";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [city, setCity] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const navigate = useNavigate();

  // Refetch featured vehicles whenever the user picks dates so unavailable ones are hidden.
  useEffect(() => {
    (async () => {
      try {
        const params = {};
        if (start) params.start_date = start.includes("T") ? start : `${start}T10:00`;
        if (end)   params.end_date   = end.includes("T")   ? end   : `${end}T18:00`;
        const r = await axios.get(`${API}/vehicles/featured`, { params });
        setVehicles(pageItems(r.data));
      } catch (e) { console.error(e); }
    })();
  }, [start, end]);

  const search = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (type) params.set("vehicle_type", type);
    // Forward date selection so VehicleDetails / Booking can prefill them.
    if (start) params.set("start", start.includes("T") ? start : `${start}T10:00`);
    if (end)   params.set("end",   end.includes("T")   ? end   : `${end}T18:00`);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      <GiveawayCampaignBanner />
      <ResumeDraftBanner />
      {/* HERO — cinematic image with gradient wash + glass search panel */}
      <section className="relative">
        {/* Cinematic background — overflow clipped here so headline descenders stay visible */}
        <div className="absolute inset-0 -z-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&w=2400&q=80')",
            }}
          />
          {/* Gradient wash to keep text legible while letting the image breathe */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(255,255,255,0.78) 35%, rgba(225,29,72,0.10) 75%, rgba(37,99,235,0.18) 100%)",
            }}
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <h1 className="zc-headline text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                Drive what you<br />
                <span className="italic" style={{ color: "var(--zc-accent)" }}>could</span>{" "}
                <span className="italic" style={{ color: "var(--zc-secondary)" }}>only</span> dream.
              </h1>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/browse" className="inline-flex items-center justify-center gap-2 px-7 h-12 zc-btn-primary text-sm whitespace-nowrap" data-testid="hero-browse-cta">
                  Browse the fleet <ArrowRight size={14} aria-hidden />
                </Link>
                <Link to="/host" className="inline-flex items-center justify-center px-6 h-12 zc-btn-ghost text-sm whitespace-nowrap" data-testid="hero-host-cta">
                  List your car
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 max-w-md">
                <Stat n="50+" l="Curated fleet" className="col-span-2 sm:col-span-1" />
                <Stat n="12k" l="Trips completed" />
                <Stat n="4.9★" l="Avg rating" />
              </div>
            </div>

            {/* Glass search panel */}
            <div className="lg:col-span-5 fade-up delay-2">
              <div className="zc-glass p-6 lg:p-7">
                <div className="eyebrow mb-4">Quick search</div>
                <form onSubmit={search} className="flex flex-col gap-3" data-testid="hero-search-form">
                  <div>
                    <label className="text-xs text-[var(--zc-text-3)] block mb-1.5">Where</label>
                    <input
                      className="zc-input h-11 px-3 text-sm w-full"
                      placeholder="City (e.g. Los Angeles)"
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      data-testid="hero-search-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <DatePicker label="Pick-up" value={start} onChange={setStart} testid="hero-search-start" helperText="Tap to open calendar." />
                    <DatePicker label="Return" value={end} onChange={setEnd} minDate={start || undefined} testid="hero-search-end" helperText="Tap to open calendar." />
                  </div>
                  <div>
                    <label className="text-xs text-[var(--zc-text-3)] block mb-1.5">Vehicle type</label>
                    <select className="zc-input h-11 px-3 text-sm w-full" value={type} onChange={e => setType(e.target.value)} data-testid="hero-search-type">
                      <option value="">Any vehicle type</option>
                      {VEHICLE_BODY_TYPES.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <button type="submit" className="h-11 zc-btn-primary text-sm flex items-center justify-center gap-2 mt-1" data-testid="hero-search-submit">
                    <Search size={14} /> Find vehicles
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-12 gap-5">
          <div className="md:col-span-7 zc-card p-8 md:p-12 relative overflow-hidden">
            <div className="absolute -right-10 -bottom-10 opacity-[0.04]">
              <Gauge size={260} />
            </div>
            <div className="eyebrow mb-4">Why Z-Cars</div>
            <h2 className="zc-headline text-4xl md:text-5xl leading-tight">
              An automotive marketplace that <span className="italic" style={{ color: "var(--zc-accent)" }}>respects</span> the machine.
            </h2>
            <p className="mt-4 text-[var(--zc-text-2)] max-w-xl">
              Verified hosts. AI-powered concierge. Loyalty token (ZCT) every trip. Real escrow. Real ratings. No filler.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/browse" className="px-4 h-10 grid place-items-center zc-btn-primary text-sm">Browse fleet</Link>
              <Link to="/host" className="px-4 h-10 grid place-items-center zc-btn-outline-primary text-sm">For hosts</Link>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-1 gap-5">
            <Pillar icon={<Sparkles size={16} />} title={`AI concierge — ${AI_NAME}`} body="Smart trip recommendations. Always on." />
            <Pillar icon={<Shield size={16} />} title="Escrow-backed bookings" body="Funds held until trip completes." color="secondary" />
            <Pillar icon={<Coins size={16} />} title="Earn ZCT loyalty" body="1× or 2× ZCT per $1 based on trips completed this year." />
          </div>
        </div>
      </section>

      {/* FEATURED FLEET */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="eyebrow mb-2">Curated fleet</div>
            <h2 className="zc-headline text-4xl md:text-5xl tracking-tight">Featured this week</h2>
          </div>
          <Link to="/browse" className="text-sm text-[var(--zc-text-2)] hover:text-[var(--zc-text)] flex items-center gap-1" data-testid="featured-view-all">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-grid">
          {vehicles.map((v, i) => <VehicleCard key={v.vehicle_id} v={v} index={i} startDate={start} endDate={end} />)}
        </div>
        {vehicles.length === 0 && (start || end) && (
          <div className="zc-card p-10 text-center text-[var(--zc-text-3)] text-sm" data-testid="featured-empty-dates">
            No vehicles available for the dates you picked. Try widening the window or browse the full fleet.
          </div>
        )}
      </section>

      <HowItWorksDemo />

      {FRACTIONAL_ENABLED && (
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="zc-card p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center" style={{ background: "linear-gradient(135deg, #FFFFFF 0%, var(--zc-secondary-soft) 100%)" }}>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--zc-secondary)] text-white zc-badge-pill mb-5">
              <Zap size={11} /> Upcoming
            </div>
            <h3 className="zc-headline text-3xl md:text-4xl leading-tight">
              Own a fraction. <span className="italic" style={{ color: "var(--zc-secondary)" }}>Share trip revenue.</span>
            </h3>
            <p className="mt-4 text-[var(--zc-text-2)] max-w-md">
              Fractional interests in premium vehicles. Booking revenue is allocated pro-rata to holders — no guaranteed returns; Curaçao-jurisdiction investors only.
            </p>
            <Link to="/invest" className="inline-flex items-center gap-2 mt-6 px-5 h-11 zc-btn-secondary text-sm" data-testid="invest-teaser-cta">
              Explore investments <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Metric n="57%" l="Investor pool share" />
            <Metric n="Pro-rata" l="Per booking" />
            <Metric n="Premium" l="Curated fleet" />
            <Metric n="Verified" l="Host network" />
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

function Stat({ n, l, className = "" }) {
  if (n == null || n === "") {
    return (
      <div className={className}>
        <div className="text-sm font-semibold leading-snug">{l}</div>
      </div>
    );
  }
  return (
    <div className={className}>
      <div className="zc-stat text-3xl">{n}</div>
      <div className="text-xs text-[var(--zc-text-3)] mt-1">{l}</div>
    </div>
  );
}

function Pillar({ icon, title, body, color = "primary" }) {
  const bg = color === "secondary" ? "bg-[var(--zc-secondary-soft)] text-[var(--zc-secondary)]" : "bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]";
  return (
    <div className="zc-card p-5 flex items-start gap-4">
      <div className={"w-10 h-10 grid place-items-center rounded-lg " + bg}>{icon}</div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-[var(--zc-text-2)] mt-1">{body}</div>
      </div>
    </div>
  );
}

function Metric({ n, l }) {
  return (
    <div className="zc-card p-5 bg-white">
      <div className="zc-stat text-2xl text-[var(--zc-secondary)]">{n}</div>
      <div className="text-xs text-[var(--zc-text-3)] mt-1">{l}</div>
    </div>
  );
}
